import { useState, useMemo } from "react";
import HeroSection from "@/components/HeroSection";
import WhoWeHelp from "@/components/WhoWeHelp";
import SocialProof from "@/components/SocialProof";
import HeroProducts from "@/components/HeroProducts";
import TestimonialsSection from "@/components/TestimonialsSection";
import BlogPreviewSection from "@/components/BlogPreviewSection";
import AboutTeaser from "@/components/AboutTeaser";
import ContactSection from "@/components/ContactSection";
import JourneyNudge from "@/components/JourneyNudge";
import { PageSEO, ProductListSchema, FAQSchema, HOMEPAGE_FAQS, MEDICAL_FAQS, LocalBusinessSchema } from "@/components/SEO";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMultiGet } from "@/hooks/useApi";

const HOME_ENDPOINTS = [
  { path: "/products", key: "products", dataKey: "products" },
  { path: "/testimonials", key: "testimonials", dataKey: "testimonials" },
  { path: "/blog", key: "blogPosts", dataKey: "posts" },
];

const ALL_FAQS = [...HOMEPAGE_FAQS, ...MEDICAL_FAQS];

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div data-testid={`faq-item-${faq.q}`} className="rounded-xl overflow-hidden transition-all duration-300" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
    <button onClick={onToggle} className="w-full flex items-center justify-between p-5 text-left">
      <span className="text-sm font-medium pr-4" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{faq.q}</span>
      {isOpen ? <ChevronUp size={16} style={{ color: "var(--ll-text-muted)" }} /> : <ChevronDown size={16} style={{ color: "var(--ll-text-muted)" }} />}
    </button>
    {isOpen && (
      <div className="px-5 pb-5">
        <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{faq.a}</p>
      </div>
    )}
  </div>
);

const HomePage = () => {
  const { data } = useMultiGet(HOME_ENDPOINTS);
  const [openFaq, setOpenFaq] = useState(null);

  const products = data.products || [];
  const testimonials = data.testimonials || [];
  const blogPosts = data.blogPosts || [];

  return (
    <main data-testid="home-page">
      <PageSEO
        title="Burnout Recovery & Mental Wellbeing for Women Professionals"
        description="From burnout to balance. Free ebooks, courses, wellness kits & journals for women professionals in South Africa. The Lemonade Living Company."
        path="/"
      />
      <ProductListSchema products={products} />
      <FAQSchema faqs={ALL_FAQS} />
      <LocalBusinessSchema />

      {/* 1. Emotional hook */}
      <HeroSection />

      {/* 2. Build identity — "Is this me?" */}
      <WhoWeHelp />

      {/* 3. Social proof — credibility */}
      <SocialProof />

      {/* 4. Testimonials — "People like me got results" */}
      <TestimonialsSection testimonials={testimonials} />

      {/* 5. Products — "Here's how" */}
      <HeroProducts products={products} />

      {/* 6. Journey nudge — deeper exploration */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pb-8">
        <JourneyNudge context="from_products" />
      </div>

      {/* 7. About — who's behind this */}
      <AboutTeaser />

      {/* 8. Blog — thought leadership */}
      <BlogPreviewSection posts={blogPosts} />

      {/* 9. FAQ — remove objections */}
      <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Frequently Asked Questions</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light mb-12" style={{ color: "var(--ll-text)" }}>
            Got <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>questions</em>?
          </h2>
          <div className="space-y-3">
            {HOMEPAGE_FAQS.map((faq, i) => (
              <FaqItem key={faq.q} faq={faq} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* 9b. Medical FAQ — health credibility & GEO */}
      <section id="health-faq" data-testid="medical-faq-section" className="py-24 md:py-32" style={{ background: "var(--ll-bg-secondary)" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Health & Wellbeing</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light mb-4" style={{ color: "var(--ll-text)" }}>
            Medical <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>FAQ</em>
          </h2>
          <p className="text-sm mb-12 leading-relaxed" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            Evidence-based answers about burnout, mental health, and recovery — reviewed for medical accuracy.
          </p>
          <div className="space-y-3">
            {MEDICAL_FAQS.map((faq, i) => (
              <FaqItem key={faq.q} faq={faq} isOpen={openFaq === `med-${i}`} onToggle={() => setOpenFaq(openFaq === `med-${i}` ? null : `med-${i}`)} />
            ))}
          </div>
        </div>
      </section>

      {/* 10. Contact */}
      <ContactSection />
    </main>
  );
};

export default HomePage;
